import {createSlice} from '@reduxjs/toolkit';
import type {
  AppPlatformWithPolicy,
  PublicConfig,
} from '../../api/actions/appConfig';
import type {AppVersionPolicy} from '../../utils/AppVersion';
import type {PublicRollout} from '../../utils/Rollout';

type StripeConfig = {
  annual_price_id: string;
  monthly_price_id: string;
  annual_trial_days: number;
};

type SliceState = {
  /** The session's flags, from `/me`. They only count while signed in. */
  flags: Record<string, boolean>;
  /**
   * The public flags, from `GET /api/app/flags`: what anyone may read,
   * signed in or out (only the backend's allow-list, never a per-user
   * grant). Both apps keep them across a sign-out (their root reducers).
   */
  publicFlags: Record<string, boolean>;
  /** The public flags' rollouts, same source (#379). */
  rollouts: Record<string, PublicRollout>;
  /** Minimum and recommended version per platform, same source (#379). */
  appVersions: Partial<Record<AppPlatformWithPolicy, AppVersionPolicy>>;
  stripeConfig: StripeConfig | null;
};

const initialState: SliceState = {
  flags: {},
  publicFlags: {},
  rollouts: {},
  appVersions: {},
  stripeConfig: null,
};

export const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    setFeatureFlags: (state, {payload}: {payload: Record<string, boolean>}) => {
      state.flags = payload;
    },
    /**
     * A successful `GET /api/app/flags` answer, replacing what was stored: a
     * key the server no longer sends is gone (a removed minimum unblocks).
     * A failed or malformed answer never reaches here (`parsePublicConfig`).
     */
    setPublicConfig: (state, {payload}: {payload: PublicConfig}) => {
      state.publicFlags = payload.flags;
      state.rollouts = payload.rollouts;
      state.appVersions = payload.appVersions;
    },
    setStripeConfig: (state, {payload}: {payload: StripeConfig | null}) => {
      state.stripeConfig = payload;
    },
  },
});

export const {setFeatureFlags, setPublicConfig, setStripeConfig} =
  featureFlagsSlice.actions;

export type {StripeConfig};

export default featureFlagsSlice.reducer;
